import * as Dialog from "@radix-ui/react-dialog";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useState } from "react";
import { MAIN_NAV, HEADER_CTAS, type NavCategory, type NavSection } from "~/config/navigation";
import { Close, ChevronDown } from "~/components/icons";

function SectionLabel({ section }: { section: NavSection }) {
  const emphasis = section.labelEmphasis;
  const emphasisIndex = emphasis ? section.label.indexOf(emphasis) : -1;

  if (!emphasis || emphasisIndex < 0) return section.label;

  return (
    <>
      {section.label.slice(0, emphasisIndex)}
      <strong className="font-bold">{emphasis}</strong>
      {section.label.slice(emphasisIndex + emphasis.length)}
    </>
  );
}

/**
 * Mobile slide-down menu using Radix Dialog.
 *
 * Supports light (default), dark (products/solutions), and engineering modes
 * to match the desktop header treatment for those routes.
 *
 * Radix handles: focus trapping, scroll locking, Escape to close,
 * click-outside-to-close, ARIA dialog role, portal rendering.
 */
export function MobileMenu({
  isOpen,
  onClose,
  isDark = false,
  isEngineering = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  isDark?: boolean;
  isEngineering?: boolean;
}) {
  const logoSrc = isDark
    ? "/images/c1-ai-logo-white.webp"
    : "/images/c1-ai-logo-black.webp";

  const panelBg = isEngineering
    ? "bg-eng-void"
    : isDark
      ? "bg-[#231F20]"
      : "bg-white";

  const textPrimary = isDark ? "text-white" : "text-gray-80";
  const textSecondary = isDark ? "text-gray-40" : "text-gray-60";
  const hoverBg = isDark ? "hover:bg-white/10" : "hover:bg-gray-7";
  const borderColor = isDark ? "border-white/10" : "border-gray-5";
  const closeColor = isDark ? "text-white/70" : "text-gray-80";

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/10 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className={`fixed left-0 right-0 top-0 z-50 max-h-screen overflow-y-auto rounded-b-2xl shadow-[0_4px_40px_rgba(0,0,0,0.12)] focus:outline-none ${panelBg}`}>
          {/* Header: logo + close */}
          {/* Mirrors the header bar it covers -- same h-16 row, same px-4 gutter,
              same 22px logo -- so the logo doesn't shift when the menu opens. */}
          <div className="flex h-16 items-center justify-between px-4">
            <a href="/">
              <img
                src={logoSrc}
                alt="C1.ai"
                className="h-[22px] w-auto"
              />
            </a>
            <Dialog.Close asChild>
              <button aria-label="Close menu">
                <Close className={`h-6 w-6 ${closeColor}`} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Title className="sr-only">Navigation Menu</Dialog.Title>

          {/* Menu items */}
          <nav className="px-4 pb-6" aria-label="Main (Mobile)">
            <ul className="space-y-1">
              {MAIN_NAV.map((cat) => (
                <MobileMenuItem
                  key={cat.id}
                  category={cat}
                  isDark={isDark}
                  isEngineering={isEngineering}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  hoverBg={hoverBg}
                  borderColor={borderColor}
                />
              ))}
            </ul>
          </nav>

          {/* CTAs */}
          <div className={`border-t ${borderColor} px-4 py-4`}>
            <div className="flex flex-col gap-3">
              <a
                href={HEADER_CTAS.bookDemo.url}
                className={`inline-flex w-full items-center justify-center rounded-lg px-4 py-2.5 font-body text-sm font-medium tracking-[-0.01em] transition-all duration-300 ease-in-out ${
                  isEngineering
                    ? "bg-eng-electric font-semibold text-eng-void hover:bg-eng-electric-light"
                    : "bg-teal-cta text-white hover:bg-teal-cta-hover"
                }`}
              >
                {HEADER_CTAS.bookDemo.label}
              </a>
              <a
                href={HEADER_CTAS.signIn.url}
                className={`inline-flex w-full items-center justify-center rounded-lg border px-4 py-2.5 font-body text-sm font-medium tracking-[-0.01em] transition-colors duration-300 ease-in-out ${
                  isEngineering
                    ? "border-eng-electric-dark bg-transparent text-eng-electric hover:bg-eng-electric-extraLight hover:text-eng-electric-light"
                    : isDark
                      ? "border-white/40 bg-transparent text-gray-20 hover:border-white hover:bg-white/10 hover:text-white"
                      : "border-teal-dark bg-transparent text-teal-dark hover:bg-teal-dark hover:text-white"
                }`}
              >
                {HEADER_CTAS.signIn.label}
              </a>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function MobileMenuItem({
  category,
  isDark,
  isEngineering,
  textPrimary,
  textSecondary,
  hoverBg,
  borderColor,
}: {
  category: NavCategory;
  isDark: boolean;
  isEngineering: boolean;
  textPrimary: string;
  textSecondary: string;
  hoverBg: string;
  borderColor: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  /*
   * Dropdown typography mirrors the desktop mega-menu (see MegaMenu.tsx's
   * MenuSection) so the same words read the same either side of the 1040px
   * breakpoint. The desktop panel expresses its dark treatment through the
   * `dark:` variant inherited from <header>; this panel renders in a Radix
   * portal outside that subtree, so the same two palettes are selected
   * explicitly off `isDark` instead.
   */
  const sectionLabelColor = isDark ? "text-eng-signal" : "text-teal-dark";
  const itemLabelColor = isDark ? "text-eng-bright" : "text-teal-dark";
  const itemDescColor = isDark ? "text-eng-signal" : "text-gray-80/70";

  // Direct link (no submenu)
  if (!category.sections) {
    return (
      <li>
        <a
          href={category.url}
          className={`block rounded-lg px-3 py-3 text-base font-semibold ${textPrimary} ${hoverBg}`}
        >
          {category.label}
        </a>
      </li>
    );
  }

  // Expandable submenu using Radix Collapsible
  return (
    <li>
      <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
        <Collapsible.Trigger className={`flex w-full items-center justify-between rounded-lg px-3 py-3 text-base font-semibold ${textPrimary} ${hoverBg}`}>
          <span>{category.label}</span>
          <ChevronDown
            className={`w-2.5 ${textSecondary} transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </Collapsible.Trigger>
        <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-out data-[state=closed]:slide-up data-[state=open]:animate-in data-[state=open]:slide-down">
          <div className={`ml-3 mt-1 space-y-5 border-l ${borderColor} pl-4`}>
            {category.sections.map((section) => (
              <div key={section.id}>
                <p className={`mb-3 font-display text-xs font-medium uppercase leading-none tracking-[1px] ${sectionLabelColor}`}>
                  <SectionLabel section={section} />
                </p>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.id}>
                      {/* The whole row is the link — no separate arrow affordance,
                          so the title, badge and description are all one tap target. */}
                      <a
                        href={item.url || "#"}
                        className={`rounded-lg px-2 py-2 ${hoverBg} ${
                          section.id === "modules" ? "flex items-start gap-3" : "block"
                        }`}
                      >
                        {section.id === "modules" && item.icon && (
                          <img
                            src={`/images/nav/icons/${item.icon}.svg`}
                            alt=""
                            className="w-7 flex-none"
                          />
                        )}
                        <span className="block min-w-0 space-y-1">
                          <span className="flex items-center gap-2">
                            <span className={`text-13 font-bold leading-tight ${itemLabelColor}`}>
                              {item.label}
                            </span>
                            {item.badge && (
                              <span className={`rounded-sm px-1 pt-px text-[9px] font-bold ${
                                isEngineering
                                  ? "bg-eng-electric text-eng-void"
                                  : "bg-teal-dark text-white"
                              }`}>
                                {item.badge}
                              </span>
                            )}
                          </span>
                          {item.description && (
                            <span className={`block text-13 leading-snug ${itemDescColor}`}>
                              {item.description}
                            </span>
                          )}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </li>
  );
}
